
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import { assertIsFn, AddContext } from "@storybook/addon-docs";

import { Canvas, Meta, Story } from '@storybook/addon-docs';
import directionsDemo from './demo/directions.twig';
import responsiveDemo from './demo/responsive.twig';
import './demo/styles.scss';
import tokens from '../../compiled/tokens/js/tokens.js';
const minimumStep = tokens.number.scale.modular.minimum_step.value;
const maximumStep = tokens.number.scale.modular.maximum_step.value;
const defaultArgTypes = {
  step: {
    type: { name: 'number' },
    control: { type: 'range', min: minimumStep, max: maximumStep, step: 1 },
  },
};
const defaultArgs = {
  step: 1,
};


const layoutProps = {
  
};
const MDXLayout = "wrapper"
function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Meta title="Utilities/Spacing" mdxType="Meta" />
    <h1>{`Spacing`}</h1>
    <p>{`While we do our best to predict whitespace needs within the context of our layout objects and components, certain situations are difficult to account for:`}</p>
    <ul>
      <li parentName="ul">{`Specific article or page content may require unique spacing.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://en.wikipedia.org/wiki/Optical_illusion"
        }}>{`Our unreliable eyes`}</a>{` may prefer alignment tweaks that defy mathematics: For example, circles may appear more visually balanced when they extend past grid lines.`}</li>
      <li parentName="ul">{`Rapid prototypes.`}</li>
    </ul>
    <p>{`Spacing utilities exist for these use cases.`}</p>
    <h2>{`Logical Properties`}</h2>
    <p>{`All of our utilities use `}<a parentName="p" {...{
        "href": "https://www.w3.org/TR/css-logical-1/"
      }}>{`logical properties`}</a>{` instead of specific directions `}<a parentName="p" {...{
        "href": "https://caniuse.com/#feat=css-logical-props"
      }}>{`in supported browsers`}</a>{` to simplify potential localization projects.`}</p>
    <p>{`The following table maps the traditional physical property for `}<inlineCode parentName="p">{`direction: ltr`}</inlineCode>{` and `}<inlineCode parentName="p">{`writing-mode: horizontal-tb`}</inlineCode>{` to its equivalent logical property:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Physical Property`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Logical Property`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`top`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`block-start`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`bottom`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`block-end`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`left`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`inline-start`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`right`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`inline-end`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <p>{`You can observe how utilities respond to various `}<inlineCode parentName="p">{`direction`}</inlineCode>{` and `}<inlineCode parentName="p">{`writing-mode`}</inlineCode>{` values by selecting a different "Text flow" in this project's toolbar.`}</p>
    <h2>{`Padding`}</h2>
    <p>{`The following utilities pad the `}<em parentName="p">{`inside`}</em>{` of the element:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`u-pad-{amount}`}</inlineCode>{` adds `}<inlineCode parentName="li">{`padding`}</inlineCode>{` to all sides.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`u-pad-block-{amount}`}</inlineCode>{` adds `}<inlineCode parentName="li">{`padding`}</inlineCode>{` to the top and bottom for horizontal writing modes.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`u-pad-block-start-{amount}`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`u-pad-block-end-{amount}`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`u-pad-inline-{amount}`}</inlineCode>{` adds `}<inlineCode parentName="li">{`padding`}</inlineCode>{` to the left and right for left-to-right directions.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`u-pad-inline-start-{amount}`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`u-pad-inline-end-{amount}`}</inlineCode></li>
    </ul>
    <p><inlineCode parentName="p">{`{amount}`}</inlineCode>{` may be a step of `}<a parentName="p" {...{
        "href": "/docs/design-modular-scale--page"
      }}>{`our modular scale`}</a>{` or `}<inlineCode parentName="p">{`none`}</inlineCode>{`.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Padding" args={defaultArgs} argTypes={defaultArgTypes} mdxType="Story">
    {args => directionsDemo({
          name: 'pad',
          ...args
        })}
  </Story>
    </Canvas>
    <h2>{`Margin`}</h2>
    <p>{`The following utilities apply space `}<em parentName="p">{`outside`}</em>{` the element:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`u-space-{amount}`}</inlineCode>{` adds `}<inlineCode parentName="li">{`margin`}</inlineCode>{` to all sides.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`u-space-block-{amount}`}</inlineCode>{` adds `}<inlineCode parentName="li">{`margin`}</inlineCode>{` to the top and bottom for horizontal writing modes.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`u-space-block-start-{amount}`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`u-space-block-end-{amount}`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`u-space-inline-{amount}`}</inlineCode>{` adds `}<inlineCode parentName="li">{`margin`}</inlineCode>{` to the left and right for left-to-right directions.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`u-space-inline-start-{amount}`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`u-space-inline-end-{amount}`}</inlineCode></li>
    </ul>
    <p><inlineCode parentName="p">{`{amount}`}</inlineCode>{` may be a step of `}<a parentName="p" {...{
        "href": "/docs/design-modular-scale--page"
      }}>{`our modular scale`}</a>{`, `}<inlineCode parentName="p">{`auto`}</inlineCode>{` or `}<inlineCode parentName="p">{`none`}</inlineCode>{`.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Margin" args={defaultArgs} argTypes={defaultArgTypes} mdxType="Story">
    {args => directionsDemo({
          name: 'space',
          ...args
        })}
  </Story>
    </Canvas>
    <p>{`To add consistent spacing between adjacent block elements, see `}<a parentName="p" {...{
        "href": "/docs/objects-rhythm--default-story"
      }}>{`the rhythm object`}</a>{`.`}</p>
    <h2>{`Negative Margin`}</h2>
    <p>{`The following utilities `}<em parentName="p">{`pull`}</em>{` an element one direction or another by applying a `}<em parentName="p">{`negative`}</em>{` `}<inlineCode parentName="p">{`margin`}</inlineCode>{` value:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`u-pull-{amount}`}</inlineCode>{` subtracts `}<inlineCode parentName="li">{`margin`}</inlineCode>{` from all sides.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`u-pull-block-{amount}`}</inlineCode>{` subtracts `}<inlineCode parentName="li">{`margin`}</inlineCode>{` from the top and bottom for horizontal writing modes.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`u-pull-block-start-{amount}`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`u-pull-block-end-{amount}`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`u-pull-inline-{amount}`}</inlineCode>{` subtracts `}<inlineCode parentName="li">{`margin`}</inlineCode>{` from the left and right for left-to-right directions.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`u-pull-inline-start-{amount}`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`u-pull-inline-end-{amount}`}</inlineCode></li>
    </ul>
    <p><inlineCode parentName="p">{`{amount}`}</inlineCode>{` may be a step of `}<a parentName="p" {...{
        "href": "/docs/design-modular-scale--page"
      }}>{`our modular scale`}</a>{`.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Negative Margin" mdxType="Story">
    {directionsDemo({
          name: 'pull',
          step: 1
        })}
  </Story>
    </Canvas>
    <h2>{`Responsive spacing`}</h2>
    <p>{`All of these utilities include responsive variations. Appending `}<inlineCode parentName="p">{`@{breakpoint}`}</inlineCode>{` to the end of the class name will cause the utility to change for the given breakpoint.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Responsive" mdxType="Story">{responsiveDemo()}</Story>
    </Canvas>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;

export const padding = (args => directionsDemo({
  name: 'pad',
  ...args
}));
padding.storyName = 'Padding';
padding.argTypes = defaultArgTypes;
padding.args = defaultArgs;
padding.parameters = { storySource: { source: 'args => directionsDemo({\n  name: \'pad\',\n  ...args\n})' } };

export const margin = (args => directionsDemo({
  name: 'space',
  ...args
}));
margin.storyName = 'Margin';
margin.argTypes = defaultArgTypes;
margin.args = defaultArgs;
margin.parameters = { storySource: { source: 'args => directionsDemo({\n  name: \'space\',\n  ...args\n})' } };

export const negativeMargin = () => (
          directionsDemo({
  name: 'pull',
  step: 1
})
        );
negativeMargin.storyName = 'Negative Margin';
negativeMargin.parameters = { storySource: { source: 'directionsDemo({\n  name: \'pull\',\n  step: 1\n})' } };

export const responsive = () => (
          responsiveDemo()
        );
responsive.storyName = 'Responsive';
responsive.parameters = { storySource: { source: 'responsiveDemo()' } };

const componentMeta = { title: 'Utilities/Spacing', includeStories: ["padding","margin","negativeMargin","responsive"],  };

const mdxStoryNameToKey = {"Padding":"padding","Margin":"margin","Negative Margin":"negativeMargin","Responsive":"responsive"};

componentMeta.parameters = componentMeta.parameters || {};
componentMeta.parameters.docs = {
  ...(componentMeta.parameters.docs || {}),
  page: () => <AddContext mdxStoryNameToKey={mdxStoryNameToKey} mdxComponentAnnotations={componentMeta}><MDXContent /></AddContext>,
};

export default componentMeta;